import React, { useState, useEffect, useRef } from 'react';

/**
 * Showcase Mockups - Preview all 4 design options for the landing page
 * Using actual AI-generated images from our platform
 */

const ShowcaseMockups = () => {
  const [activeOption, setActiveOption] = useState(4); // Default to Hybrid
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxVideo, setLightboxVideo] = useState(null);
  const scrollRef = useRef(null);
  const hybridScrollRef = useRef(null);
  const videoRefs = useRef({});
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  // Sample generated content - using actual images and videos we created
  const showcaseItems = [
    {
      id: 1,
      type: 'image',
      title: 'Tandoor Palace - Restaurant',
      model: 'Ultra Quality',
      url: 'https://tempfile.aiquickdraw.com/s/faed1ef97a31c8ebaba56432ad091c42_0_1770689796_9613.png',
      category: 'Restaurant'
    },
    {
      id: 2,
      type: 'image',
      title: 'Tandoor Palace - Restaurant',
      model: 'Premium Quality',
      url: 'https://tempfile.aiquickdraw.com/images/1770689819235-qq1m7flvh19.jpeg',
      category: 'Restaurant'
    },
    {
      id: 3,
      type: 'image',
      title: 'MindfulMe - Wellness App',
      model: 'Ultra Quality',
      url: 'https://tempfile.aiquickdraw.com/s/8774561452991bfc75c5391e1392ee9f_0_1770690030_7741.png',
      category: 'Mobile App'
    },
    {
      id: 4,
      type: 'image',
      title: 'MindfulMe - Wellness App',
      model: 'Premium Quality',
      url: 'https://tempfile.aiquickdraw.com/images/1770690483224-twrs21o4jbg.jpeg',
      category: 'Mobile App'
    },
    {
      id: 5,
      type: 'image',
      title: 'CloudScale Solutions',
      model: 'Premium Quality',
      url: 'https://tempfile.aiquickdraw.com/images/1770690526971-vppkb93s0x.jpeg',
      category: 'Service'
    },
    {
      id: 6,
      type: 'image',
      title: 'ShopifyX Pro - E-commerce',
      model: 'Ultra Quality',
      url: 'https://tempfile.aiquickdraw.com/s/e365de19656a3e83182b651b4be95f93_0_1770689054_5539.png',
      category: 'E-commerce'
    },
    {
      id: 7,
      type: 'video',
      title: 'Product Promo Video',
      model: 'Ultra Video',
      url: 'https://cdn.pixabay.com/video/2024/05/31/214971_large.mp4',
      thumbnail: 'https://tempfile.aiquickdraw.com/s/faed1ef97a31c8ebaba56432ad091c42_0_1770689796_9613.png',
      category: 'Video'
    },
    {
      id: 8,
      type: 'image',
      title: 'ShopifyX Pro - E-commerce',
      model: '4K Ultra HD',
      url: 'https://tempfile.aiquickdraw.com/image-format-converter/1770689136698-8afx5qhxokl.jpg',
      category: 'E-commerce'
    },
    {
      id: 9,
      type: 'video',
      title: 'Tech Product Video',
      model: 'Premium Video',
      url: 'https://cdn.pixabay.com/video/2020/05/25/40130-424930032_large.mp4',
      thumbnail: 'https://tempfile.aiquickdraw.com/images/1770690483224-twrs21o4jbg.jpeg',
      category: 'Video'
    },
    {
      id: 10,
      type: 'image',
      title: 'FitPro 360 - Fitness',
      model: 'Ultra Quality',
      url: 'https://tempfile.aiquickdraw.com/s/5b306de38966a0c0ec49ce42f8d4e054_0_1770688403_2446.png',
      category: 'Mobile App'
    }
  ];

  const filteredItems = selectedFilter === 'all' 
    ? showcaseItems 
    : showcaseItems.filter(item => item.type === selectedFilter || item.category.toLowerCase() === selectedFilter.toLowerCase());

  // Auto-scroll for Option 1 and Hybrid
  useEffect(() => {
    const scrollContainer = activeOption === 1 ? scrollRef.current : hybridScrollRef.current;
    
    if ((activeOption === 1 || activeOption === 4) && scrollContainer && !isAutoScrollPaused) {
      let scrollAmount = 0;
      const scrollSpeed = 0.8;
      
      const autoScroll = setInterval(() => {
        scrollAmount += scrollSpeed;
        if (scrollAmount >= scrollContainer.scrollWidth - scrollContainer.clientWidth) {
          scrollAmount = 0;
        }
        scrollContainer.scrollLeft = scrollAmount;
      }, 30);

      const handleMouseEnter = () => setIsAutoScrollPaused(true);
      const handleMouseLeave = () => setIsAutoScrollPaused(false);
      
      scrollContainer.addEventListener('mouseenter', handleMouseEnter);
      scrollContainer.addEventListener('mouseleave', handleMouseLeave);
      
      return () => {
        clearInterval(autoScroll);
        scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
        scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [activeOption, isAutoScrollPaused]);

  // Video auto-play on scroll (Intersection Observer)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {}); // Auto-play when visible
          } else {
            video.pause(); // Pause when not visible
          }
        });
      },
      { threshold: 0.5 } // Play when 50% visible
    );

    // Observe all video elements
    Object.values(videoRefs.current).forEach((video) => {
      if (video) observer.observe(video);
    });

    return () => observer.disconnect();
  }, [activeOption, filteredItems]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
            Landing Page Showcase Mockups
          </h1>
          <p className="text-gray-400 text-sm mt-1">Preview all 4 design options with real AI-generated content</p>
        </div>
      </div>

      {/* Option Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-3 mb-8">
          {[1, 2, 3, 4].map(option => (
            <button
              key={option}
              onClick={() => setActiveOption(option)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                activeOption === option
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg shadow-orange-500/30'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              Option {option}: {
                option === 1 ? 'Infinite Scroll' :
                option === 2 ? 'Masonry Grid' :
                option === 3 ? 'Before/After' :
                'Hybrid'
              }
            </button>
          ))}
        </div>

        {/* Option 1: Infinite Horizontal Scroll */}
        {activeOption === 1 && (
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-3xl p-8 border border-white/10">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium mb-4">
                OPTION 1
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                See What Our AI Creates
              </h2>
              <p className="text-gray-400 text-lg">Real examples generated in seconds</p>
            </div>

            {/* Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {['all', 'image', 'video'].map(filter => (
                <button
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedFilter === filter
                      ? 'bg-white text-gray-900'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {filter === 'all' ? 'All' : filter === 'image' ? 'Images' : 'Videos'}
                </button>
              ))}
            </div>

            {/* Horizontal Scroll Container */}
            <div 
              ref={scrollRef}
              className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {[...filteredItems, ...filteredItems].map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  onClick={() => setLightboxImage(item.url)}
                  className="flex-shrink-0 w-72 group cursor-pointer"
                >
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-yellow-500/20">
                    <img 
                      src={item.type === 'video' ? item.thumbnail : item.url} 
                      alt={item.title}
                      className="w-full h-48 object-cover"
                    />
                    {item.type === 'video' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <p className="text-white font-semibold text-sm">{item.title}</p>
                      <p className="text-gray-300 text-xs">{item.model}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-center text-gray-500 text-sm mt-4">← Auto-scrolling • Hover to pause • Click to expand →</p>
          </div>
        )}

        {/* Option 2: Masonry Grid */}
        {activeOption === 2 && (
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-3xl p-8 border border-white/10">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-medium mb-4">
                OPTION 2
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                Stunning Content Created by AI
              </h2>
              <p className="text-gray-400 text-lg">Click any image to view full size</p>
            </div>

            {/* Masonry Grid */}
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
              {filteredItems.map((item, index) => (
                <div
                  key={item.id}
                  onClick={() => setLightboxImage(item.url)}
                  className={`break-inside-avoid group cursor-pointer ${
                    index % 3 === 0 ? 'row-span-2' : ''
                  }`}
                >
                  <div className="relative rounded-2xl overflow-hidden shadow-xl transform transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-cyan-500/20">
                    <img 
                      src={item.type === 'video' ? item.thumbnail : item.url} 
                      alt={item.title}
                      className={`w-full object-cover ${index % 3 === 0 ? 'h-80' : 'h-52'}`}
                    />
                    {item.type === 'video' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <div>
                        <p className="text-white font-semibold">{item.title}</p>
                        <p className="text-cyan-300 text-sm">Generated with {item.model}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Option 3: Before/After Showcase */}
        {activeOption === 3 && (
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-3xl p-8 border border-white/10">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium mb-4">
                OPTION 3
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                From Product to Campaign in 60 Seconds
              </h2>
              <p className="text-gray-400 text-lg">See the transformation</p>
            </div>

            {/* Before/After Cards */}
            <div className="space-y-8">
              {[
                {
                  input: { name: 'Tandoor Palace', type: 'Restaurant', desc: 'Award-winning Indian cuisine' },
                  outputs: showcaseItems.filter(i => i.category === 'Restaurant').slice(0, 2)
                },
                {
                  input: { name: 'MindfulMe', type: 'Mobile App', desc: 'AI meditation & wellness' },
                  outputs: showcaseItems.filter(i => i.category === 'Mobile App').slice(0, 2)
                },
                {
                  input: { name: 'ShopifyX Pro', type: 'E-commerce', desc: 'Online store platform' },
                  outputs: showcaseItems.filter(i => i.category === 'E-commerce').slice(0, 2)
                }
              ].map((showcase, idx) => (
                <div key={idx} className="bg-white/5 rounded-2xl p-6 border border-white/10">
                  <div className="grid md:grid-cols-5 gap-6 items-center">
                    {/* Input */}
                    <div className="md:col-span-1">
                      <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl p-4 text-center">
                        <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                          <span className="text-2xl">📦</span>
                        </div>
                        <h4 className="font-bold text-white">{showcase.input.name}</h4>
                        <p className="text-xs text-gray-400 mt-1">{showcase.input.type}</p>
                        <p className="text-xs text-gray-500 mt-1">{showcase.input.desc}</p>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="hidden md:flex md:col-span-1 justify-center">
                      <div className="flex items-center">
                        <div className="w-16 h-0.5 bg-gradient-to-r from-yellow-500 to-orange-500"></div>
                        <svg className="w-6 h-6 text-orange-500 -ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                        </svg>
                      </div>
                    </div>

                    {/* Outputs */}
                    <div className="md:col-span-3">
                      <div className="grid grid-cols-2 gap-4">
                        {showcase.outputs.map(item => (
                          <div 
                            key={item.id}
                            onClick={() => setLightboxImage(item.url)}
                            className="relative rounded-xl overflow-hidden cursor-pointer transform transition-all hover:scale-105"
                          >
                            <img src={item.url} alt={item.title} className="w-full h-32 object-cover" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                              <p className="text-xs text-white">{item.model}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex flex-wrap justify-center gap-4 mt-4 pt-4 border-t border-white/10">
                    <span className="flex items-center gap-2 text-sm text-gray-300">
                      <span className="text-lg">🖼️</span> 4 Images
                    </span>
                    <span className="flex items-center gap-2 text-sm text-gray-300">
                      <span className="text-lg">🎬</span> 2 Videos
                    </span>
                    <span className="flex items-center gap-2 text-sm text-gray-300">
                      <span className="text-lg">📝</span> 8 Text Ads
                    </span>
                    <span className="flex items-center gap-2 text-sm text-green-400 font-semibold">
                      <span className="text-lg">⏱️</span> 47 seconds
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Option 4: Hybrid */}
        {activeOption === 4 && (
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-3xl p-8 border border-white/10">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium mb-4">
                OPTION 4 - HYBRID
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                AI-Powered Marketing Magic
              </h2>
              <p className="text-gray-400 text-lg">The best of all worlds</p>
            </div>

            {/* Hero Showcase - Large Featured Video or Image */}
            <div className="mb-8">
              {showcaseItems.find(i => i.type === 'video') ? (
                <div className="relative rounded-3xl overflow-hidden group">
                  <video
                    ref={(el) => (videoRefs.current['hero'] = el)}
                    src={showcaseItems.find(i => i.type === 'video').url}
                    className="w-full h-64 md:h-96 object-cover"
                    muted
                    loop
                    playsInline
                    poster={showcaseItems.find(i => i.type === 'video').thumbnail}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none">
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <span className="inline-block px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full mb-3 animate-pulse">
                        LIVE VIDEO
                      </span>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        {showcaseItems.find(i => i.type === 'video').title}
                      </h3>
                      <p className="text-gray-300">Generated with {showcaseItems.find(i => i.type === 'video').model} - Auto-playing</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div 
                  onClick={() => setLightboxImage(showcaseItems[0].url)}
                  className="relative rounded-3xl overflow-hidden cursor-pointer group"
                >
                  <img 
                    src={showcaseItems[0].url} 
                    alt="Featured" 
                    className="w-full h-64 md:h-96 object-cover transform transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <span className="inline-block px-3 py-1 bg-yellow-500 text-black text-xs font-bold rounded-full mb-3">
                        FEATURED
                      </span>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{showcaseItems[0].title}</h3>
                      <p className="text-gray-300">Generated with {showcaseItems[0].model} in seconds</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Auto-Scrolling Horizontal Row */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  <span className="text-yellow-400">✨</span> More Examples
                </h3>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  {isAutoScrollPaused ? (
                    <>
                      <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                      Paused - Hover to browse
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Auto-scrolling
                    </>
                  )}
                </span>
              </div>
              
              <div 
                ref={hybridScrollRef}
                className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {[...showcaseItems, ...showcaseItems].map((item, index) => (
                  <div
                    key={`hybrid-${item.id}-${index}`}
                    onClick={() => item.type === 'video' ? setLightboxVideo(item.url) : setLightboxImage(item.url)}
                    className="flex-shrink-0 w-64 cursor-pointer group"
                  >
                    <div className="relative rounded-xl overflow-hidden transform transition-all duration-300 group-hover:scale-105 shadow-lg group-hover:shadow-purple-500/30">
                      {item.type === 'video' ? (
                        <>
                          <video
                            ref={(el) => (videoRefs.current[`scroll-${item.id}-${index}`] = el)}
                            src={item.url}
                            className="w-full h-40 object-cover"
                            muted
                            loop
                            playsInline
                            poster={item.thumbnail}
                          />
                          <div className="absolute top-2 right-2">
                            <span className="px-2 py-1 bg-red-500/90 text-white text-xs font-bold rounded-full flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                              VIDEO
                            </span>
                          </div>
                        </>
                      ) : (
                        <img 
                          src={item.url} 
                          alt={item.title}
                          className="w-full h-40 object-cover"
                        />
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                        <p className="text-sm text-white font-medium truncate">{item.title}</p>
                        <p className="text-xs text-gray-400">{item.model}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Bar */}
            <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-2xl p-6 border border-yellow-500/20">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold text-yellow-400">10K+</p>
                  <p className="text-sm text-gray-400">Images Generated</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-orange-400">5K+</p>
                  <p className="text-sm text-gray-400">Videos Created</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-cyan-400">50K+</p>
                  <p className="text-sm text-gray-400">Text Ads Written</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-400">99%</p>
                  <p className="text-sm text-gray-400">Cost Savings</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recommendation */}
        <div className="mt-8 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-2xl p-6 border border-yellow-500/20">
          <h3 className="text-lg font-semibold text-yellow-400 mb-2">💡 Recommendation</h3>
          <p className="text-gray-300">
            <strong>Option 4 (Hybrid)</strong> provides the best user experience by combining:
            a featured hero image, horizontal scroll for browsing, and stats for social proof.
            It's visually engaging and showcases your AI capabilities effectively.
          </p>
        </div>
      </div>

      {/* Lightbox for Images */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={() => setLightboxImage(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img 
            src={lightboxImage} 
            alt="Full size" 
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Lightbox for Videos */}
      {lightboxVideo && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxVideo(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
            onClick={() => setLightboxVideo(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <video 
            src={lightboxVideo} 
            className="max-w-full max-h-[90vh] rounded-lg"
            controls
            autoPlay
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* CSS for hiding scrollbar */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ShowcaseMockups;
